// Pure, deterministic theme engine: one hex in → WCAG-checked color tokens out.
export const DEFAULT_THEME_COLOR = "#1B2A4A";
export const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const LIGHT = "#FFFFFF";
const DARK = "#0B0B0C";

export function normalizeHex(hex) {
  if (typeof hex !== "string" || !HEX_RE.test(hex.trim())) return null;
  let h = hex.trim().slice(1).toLowerCase();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return `#${h}`;
}

const toRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const toHex = (rgb) => `#${rgb.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0")).join("")}`;

export function luminance(hex) {
  const [r, g, b] = toRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a, b) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

function toHsl(hex) {
  const [r, g, b] = toRgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h / 6, s, l];
}

function fromHsl([h, s, l]) {
  if (s === 0) return toHex([l * 255, l * 255, l * 255]);
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const f = (t) => {
    const x = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    if (x < 1 / 6) return p + (q - p) * 6 * x;
    if (x < 1 / 2) return q;
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
    return p;
  };
  return toHex([f(h + 1 / 3) * 255, f(h) * 255, f(h - 1 / 3) * 255]);
}

export const mix = (a, b, t) => {
  const [x, y] = [toRgb(a), toRgb(b)];
  return toHex(x.map((v, i) => v + (y[i] - v) * t));
};

// Moves lightness toward `dir` (+1 lighter, -1 darker) until every check passes.
function adjust(hex, dir, passes) {
  let [h, s, l] = toHsl(hex);
  let out = hex;
  for (let i = 0; i < 60 && !passes(out); i += 1) {
    l = Math.min(1, Math.max(0, l + dir * 0.02));
    out = fromHsl([h, s, l]);
  }
  return out;
}

const minContrast = (c, others) => Math.min(...others.map((o) => contrast(c, o)));

export function generateTheme(input) {
  const bg = normalizeHex(input) || DEFAULT_THEME_COLOR;
  let text = contrast(LIGHT, bg) >= contrast(DARK, bg) ? LIGHT : DARK;
  if (contrast(text, bg) < 4.5) text = contrast(LIGHT, bg) >= contrast("#000000", bg) ? LIGHT : "#000000";
  const lightText = text === LIGHT;
  const away = lightText ? -1 : 1;
  const toward = -away;
  const bgSecondary = adjust(mix(bg, lightText ? "#000000" : "#FFFFFF", 0.12), away, (c) => contrast(text, c) >= 4.5);

  const surface = adjust(mix(bg, text, 0.08), away, (c) => contrast(text, c) >= 4.5);
  const readable = [bg, bgSecondary, surface];
  const textSecondary = adjust(mix(text, bg, 0.3), toward, (c) => minContrast(c, readable) >= 4.5);
  const surfaceBorder = adjust(mix(surface, text, 0.3), toward, (c) => contrast(c, surface) >= 3);
  const divider = adjust(mix(bg, text, 0.3), toward, (c) => minContrast(c, [bg, surface]) >= 3);
  const accent = adjust(bg, toward, (c) => minContrast(c, [bg, surface]) >= 3);
  const link = adjust(accent, toward, (c) => minContrast(c, readable) >= 4.5);
  const metricHighlight = adjust(accent, toward, (c) => contrast(c, surface) >= 4.5);
  const iconBg = text;
  const icon = adjust(bg, away, (c) => contrast(c, iconBg) >= 3);
  const buttonBg = text;
  const buttonText = adjust(bg, away, (c) => contrast(c, buttonBg) >= 4.5);

  return {
    bg, bgSecondary, surface, surfaceBorder, text, textSecondary, heading: text,
    icon, iconBg, buttonBg, buttonText, link, divider, accent, metricHighlight,
    qrFg: DARK, qrBg: LIGHT,
  };
}

export const themeToCssVars = (t) =>
  Object.fromEntries(Object.entries(t).map(([k, v]) => [`--pp-${k.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`, v]));
